<template>
  <article
    class="preview-bloco"
    :class="classes"
    :style="estiloContainer"
  >
    <img
      v-if="imagemLateral"
      :src="item.imagem_fundo"
      alt=""
      class="preview-bloco__imagem"
      :class="imagemHorizontal ? 'preview-bloco__imagem--horizontal' : 'preview-bloco__imagem--vertical'"
    />
    <div class="preview-bloco__conteudo">
      <h3 v-if="item.titulo" class="preview-bloco__titulo">{{ item.titulo }}</h3>
      <h3 v-else-if="!compacto" class="preview-bloco__titulo preview-bloco__titulo--vazio">
        Título do bloco
      </h3>
      <p v-if="item.subtitulo && !compacto" class="preview-bloco__subtitulo">{{ item.subtitulo }}</p>
      <p v-if="item.resumo && !compacto" class="preview-bloco__resumo">{{ item.resumo }}</p>
      <span
        v-if="estilo.cta_texto && !compacto"
        class="preview-bloco__cta"
        :style="{
          backgroundColor: estilo.cta_cor_fundo || '#dc3545',
          color: estilo.cta_cor_texto || '#ffffff',
        }"
      >
        {{ estilo.cta_texto }}
      </span>
    </div>
  </article>
</template>

<script>
import { corEscura } from "../helpers/conteudoLabels";

const DIRECAO_POR_POSICAO = {
  esquerda: "row",
  direita: "row-reverse",
  cima: "column",
  baixo: "column-reverse",
};

export default {
  name: "PreviewBlocoTexto",
  props: {
    item: {
      type: Object,
      required: true,
    },
    compacto: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    estilo() {
      return this.item.estilo || {};
    },
    posicaoImagem() {
      return this.estilo.posicao_imagem || "fundo";
    },
    imagemComoFundo() {
      return !!this.item.imagem_fundo && this.posicaoImagem === "fundo";
    },
    imagemLateral() {
      return !!this.item.imagem_fundo && this.posicaoImagem !== "fundo" && !this.compacto;
    },
    imagemHorizontal() {
      return this.posicaoImagem === "esquerda" || this.posicaoImagem === "direita";
    },
    fundoCustomizado() {
      return (this.estilo.fundo_tipo || "nenhum") !== "nenhum" && !!this.estilo.cor_fundo;
    },
    fundoEscuro() {
      return (
        this.fundoCustomizado &&
        (corEscura(this.estilo.cor_fundo) ||
          (this.estilo.fundo_tipo === "gradiente" && corEscura(this.estilo.cor_fundo_final)))
      );
    },
    classes() {
      return {
        "preview-bloco--compacto": this.compacto,
        "preview-bloco--imagem-fundo": this.imagemComoFundo && !this.fundoCustomizado,
        "preview-bloco--fundo-escuro": this.fundoEscuro,
        "preview-bloco--imagem-lateral": this.imagemLateral,
        [`preview-bloco--${this.estilo.alinhamento_texto || "esquerda"}`]: true,
        [`preview-bloco--${this.estilo.tamanho_texto || "medio"}`]: true,
      };
    },
    estiloContainer() {
      const estilo = {};
      const camadas = [];

      if (this.estilo.fundo_tipo === "solido" && this.estilo.cor_fundo) {
        estilo.backgroundColor = this.estilo.cor_fundo;
      }

      if (this.imagemComoFundo) {
        camadas.push(`url(${this.item.imagem_fundo})`);
      }

      if (this.estilo.fundo_tipo === "gradiente" && this.estilo.cor_fundo) {
        camadas.push(
          `linear-gradient(135deg, ${this.estilo.cor_fundo}, ${this.estilo.cor_fundo_final || this.estilo.cor_fundo})`
        );
      }

      if (camadas.length > 0) {
        estilo.backgroundImage = camadas.join(", ");
      }

      if (this.estilo.cantos_arredondados === false) {
        estilo.borderRadius = 0;
      }

      if (this.estilo.usa_borda) {
        estilo.border = `${this.estilo.espessura_borda || 1}px solid ${this.estilo.cor_borda || "#dee2e6"}`;
      }

      if (this.imagemLateral) {
        estilo.flexDirection = DIRECAO_POR_POSICAO[this.posicaoImagem];
      }

      return estilo;
    },
  },
};
</script>

<style scoped>
.preview-bloco {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  min-height: 140px;
  padding: 20px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  background-color: #fff;
  background-size: cover;
  background-position: center;
  overflow: hidden;
  color: #212529;
}

.preview-bloco__conteudo {
  position: relative;
  z-index: 1;
}

.preview-bloco__titulo {
  font-size: 18px;
  font-weight: 700;
  margin: 0 0 4px;
  color: inherit;
}

.preview-bloco__titulo--vazio {
  opacity: 0.35;
}

.preview-bloco__subtitulo {
  font-size: 14px;
  font-weight: 600;
  margin: 0 0 4px;
  opacity: 0.85;
}

.preview-bloco__resumo {
  font-size: 13px;
  margin: 0;
  opacity: 0.75;
  white-space: pre-line;
}

.preview-bloco__cta {
  display: inline-block;
  margin-top: 12px;
  padding: 9px 24px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  line-height: 1;
}

.preview-bloco--imagem-fundo {
  border: none;
  min-height: 180px;
  color: #fff;
}

.preview-bloco--imagem-fundo::before {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0.05) 0%, rgba(0, 0, 0, 0.65) 100%);
}

.preview-bloco--fundo-escuro {
  border: none;
  color: #fff;
}

.preview-bloco--imagem-lateral {
  justify-content: flex-start;
  gap: 14px;
  padding: 16px;
}

.preview-bloco--imagem-lateral .preview-bloco__conteudo {
  flex: 1;
  align-self: center;
  min-width: 0;
}

.preview-bloco__imagem {
  border-radius: 6px;
  object-fit: cover;
}

.preview-bloco__imagem--horizontal {
  width: 42%;
  align-self: stretch;
  min-height: 100px;
}

.preview-bloco__imagem--vertical {
  width: 100%;
  max-height: 150px;
}

.preview-bloco--esquerda { text-align: left; }
.preview-bloco--centro { text-align: center; }
.preview-bloco--direita { text-align: right; }

.preview-bloco--pequeno .preview-bloco__titulo { font-size: 15px; }
.preview-bloco--pequeno .preview-bloco__subtitulo { font-size: 13px; }
.preview-bloco--pequeno .preview-bloco__resumo { font-size: 12px; }
.preview-bloco--grande .preview-bloco__titulo { font-size: 28px; }
.preview-bloco--grande .preview-bloco__subtitulo { font-size: 17px; }
.preview-bloco--grande .preview-bloco__resumo { font-size: 15px; }
.preview-bloco--gigante .preview-bloco__titulo { font-size: 36px; }
.preview-bloco--gigante .preview-bloco__subtitulo { font-size: 20px; }
.preview-bloco--gigante .preview-bloco__resumo { font-size: 16px; }

.preview-bloco--compacto {
  min-height: 40px;
  height: 40px;
  width: 90px;
  padding: 4px 6px;
  border-radius: 4px;
  justify-content: center;
}

.preview-bloco--compacto .preview-bloco__titulo {
  font-size: 9px;
  line-height: 1.2;
  margin: 0;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.preview-bloco--compacto.preview-bloco--imagem-fundo {
  min-height: 40px;
}
</style>
